import { z } from "zod";

import { AdjustmentType, ProductType } from "@/interfaces/tools.interface";
import {
  basicValidator,
  fileOrStringArrayValidator,
  reactSelectValidator,
} from "@/lib/validators";

const baseProductSchema = z.object({
  name: basicValidator(true, "Required"),
  files: fileOrStringArrayValidator,
  images: fileOrStringArrayValidator,
});

const productSchema = baseProductSchema.extend({
  type: z.nativeEnum(ProductType),
  quantity: z.coerce.number().min(1, {
    message: "Required",
  }),
});

const baseSubProductSchema = z.object({
  name: basicValidator(),
  modelNumber: basicValidator(),
  images: fileOrStringArrayValidator,
});

const subproductItemSchema = z.object({
  isBulkUpload: z.boolean().default(false),
  items: z.array(baseSubProductSchema),
});

const subProductSchema = baseProductSchema.extend({
  modelNumber: basicValidator(),
});

export const createProductSchema = productSchema;
export const updateProductSchema = productSchema.partial().extend({
  id: z.string().uuid(),
});

export const createSubProductSchema = subProductSchema;
export const updateSubProductSchema = subProductSchema.partial().extend({
  id: z.string().uuid(),
});

export const adjustProductQuantitySchema = z
  .object({
    quantity: z.coerce.number().min(1, "Required"),
    existingQuantity: z.coerce.number().optional(),
    action: z.nativeEnum(AdjustmentType),
    remarks: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.action === "REDUCE") {
      if (data?.existingQuantity === 0) {
        ctx.addIssue({
          message: "Existing quantity is 0. Please add quantity",
          path: ["quantity"],
          code: "custom",
        });
      } else if (
        data.existingQuantity &&
        data.quantity > data.existingQuantity
      ) {
        ctx.addIssue({
          message: "Quantity should not be greater than existing quantity",
          path: ["quantity"],
          code: "custom",
        });
      }
    }
  });

export const createSubProductItemSchema = subproductItemSchema;
export const updateSubProductItemSchema = baseSubProductSchema.extend({
  id: z.string().uuid(),
});

export const baseAssignToolItemSchema = z
  .object({
    productId: z.string().uuid(),
    type: z.nativeEnum(ProductType),
    quantity: z.coerce.number().optional(),
    subProductIds: z.array(z.string().uuid()).optional(),
    note: z.string().optional(),
    description: z.string().optional(),
    isSelected: z.boolean().default(false).optional(),
  })
  .superRefine((data, ctx) => {
    if (!data?.isSelected) {
      return;
    }
    if (!data?.quantity || data.quantity <= 0) {
      ctx.addIssue({
        message: "Required",
        path: ["quantity"],
        code: "custom",
      });
    }
    if (!data?.quantity) {
      return;
    }
    if (data?.type === ProductType.Has_Quantity) {
      return;
    }
    if (!data?.subProductIds?.length) {
      ctx.addIssue({
        message: "Required",
        path: ["subProductIds"],
        code: "custom",
      });
    } else if (
      data.quantity > data?.subProductIds?.length ||
      data.quantity < data?.subProductIds?.length
    ) {
      ctx.addIssue({
        message: "Quantity should be equal to selected sub-products",
        path: ["quantity"],
        code: "custom",
      });
    }
  });

export const assignToolsSchema = z.object({
  items: z.array(baseAssignToolItemSchema),
});

export const awaitingToolsSchema = z.object({
  items: z.array(
    z.object({
      id: z.string().uuid(),
      quantity: z.coerce.number(),
      subProducts: z.array(z.string().uuid()),
      notes: z.string(),
      comments: z.string(),
    }),
  ),
});

export const updateAwaitingToolsSchema = z.object({
  items: z.array(
    z.object({
      productDetailId: z.string().uuid(),
      productAssignments: z.array(
        z
          .object({
            id: z.string().uuid(),
            type: z.nativeEnum(ProductType),
            quantity: z.coerce.number().min(1, { message: "Required" }),
            subProductIds: reactSelectValidator("Required", true),
            note: basicValidator(false),
            description: basicValidator(false),
            isAltered: z.boolean().default(false),
          })
          .superRefine((data, ctx) => {
            if (!data?.isAltered) {
              return;
            }
            if (data?.type === ProductType.Has_Quantity) {
              return;
            }
            if (!data?.subProductIds || data?.subProductIds.length === 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Required",
                path: ["subProductIds"],
              });
            } else if (
              data?.quantity > data?.subProductIds.length ||
              data?.quantity < data?.subProductIds.length
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  "Quantity must be equal to number selected sub products",
                path: ["quantity"],
              });
            }
          }),
      ),
    }),
  ),
});

export type TUpdateAwaitingTools = z.infer<typeof updateAwaitingToolsSchema>;

export const projectToolSchema = z
  .object({
    productAssignmentId: z.string().uuid(),
    productId: z.string().uuid(),
    quantity: z.coerce.number(),
    subProductAssignmentIds: reactSelectValidator("Required", true),
    subProductIds: reactSelectValidator("Required", true),
    prevSubProductIds: z.array(z.string().uuid()).optional(),
    note: z.string().optional(),
    description: z.string().optional(),
    type: z.nativeEnum(ProductType).optional(),
    isSelected: z.boolean().optional().default(false),
    isAltered: z.boolean().optional().default(false),
  })
  .superRefine((data, ctx) => {
    if (!data?.isSelected) {
      return;
    }
    if (!data?.quantity || data?.quantity === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["quantity"],
      });
    }
    if (data?.type === ProductType.Has_Sub_Product) {
      //Check if the quantity is equals to subProductIds.length
      if (data?.quantity && data?.subProductIds) {
        if (data.quantity !== data.subProductIds.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Quantity should be equal to selected sub-products",
            path: ["quantity"],
          });
        }
      } else if (data?.quantity && !data?.subProductIds) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["subProductIds"],
        });
      }
    }
  });
export const createTransferToolSchema = z.object({
  projectToId: z.string().uuid({
    message: "Required",
  }),
  projectFromId: z.string().uuid({
    message: "Required",
  }),
  products: z.array(projectToolSchema),
  transferredTools: z.array(z.string().uuid()).optional(),
});

export const assignRequestedProductToProjectSchema = z.object({
  items: z.array(baseAssignToolItemSchema),
});

export const requestReturnApproveSchema = z.object({
  items: z.array(
    z
      .object({
        productAssignmentId: z.string().uuid(),
        productId: z.string().uuid(),
        quantity: z.coerce.number().min(1, {
          message: "Required",
        }),
        returnItemId: z.string().uuid(),
        subProductIds: reactSelectValidator("Required", true),
        remarks: z.string().optional(),
        type: z.nativeEnum(ProductType),
      })
      .superRefine((data, ctx) => {
        if (data.type === ProductType.Has_Quantity) {
          return;
        }

        if (!data?.subProductIds?.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Required",
            path: ["subProductIds"],
          });
        }

        if (
          data?.quantity > (data?.subProductIds?.length ?? 0) ||
          data?.quantity < (data?.subProductIds?.length ?? 0)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Quantity must match the number of selected sub-products.",
            path: ["quantity"],
          });
        }
      }),
  ),
});

export type TAddProduct = z.infer<typeof createProductSchema>;
export type TUpdateProduct = z.infer<typeof updateProductSchema>;
export type TAddSubProduct = z.infer<typeof createSubProductSchema>;
export type TUpdateSubProduct = z.infer<typeof updateSubProductSchema>;
export type TAddSubProductItem = z.infer<typeof createSubProductItemSchema>;
export type TUpdateSubProductItem = z.infer<typeof updateSubProductItemSchema>;
export type TAdjustProductQuantity = z.infer<
  typeof adjustProductQuantitySchema
>;
export type TAssignTools = z.infer<typeof assignToolsSchema>;
export type TCreateTransfer = z.infer<typeof createTransferToolSchema>;
export type TAssignRequestedProductToProject = z.infer<
  typeof assignRequestedProductToProjectSchema
>;
export type TReturnRequestApproval = z.infer<typeof requestReturnApproveSchema>;

export const defaultProduct: TAddProduct = {
  name: "",
  files: [],
  type: ProductType.Has_Quantity,
  images: [],
  quantity: 0,
};

export const defaultSubProduct: TAddSubProduct = {
  name: "",
  files: [],
  images: [],
  modelNumber: "",
};

export const emptySubProductItems = {
  name: undefined,
  model: undefined,
  images: [],
};

export const defaultSubProductItem: TAddSubProductItem = {
  isBulkUpload: false,
  items: [emptySubProductItems],
};

export const defaultAssignToolItem = {
  productId: "",
  quantity: 0,
  subProductIds: undefined,
  note: undefined,
  comment: undefined,
  type: ProductType.Has_Quantity,
};

export const defaultAssignTools: TAssignTools = {
  items: [defaultAssignToolItem],
};

export const defaultTransferToolItem: z.infer<typeof projectToolSchema> = {
  productId: "",
  productAssignmentId: "",
  quantity: 0,
  subProductIds: undefined,
  note: undefined,
  description: undefined,
  type: ProductType.Has_Quantity,
  isSelected: false,
  isAltered: false,
};

export const defaultTransferTools: TCreateTransfer = {
  projectToId: "",
  projectFromId: "",
  products: [],
  transferredTools: [],
};
